import { isEmpty } from 'lodash';
import Http from '@/services/http';
import {
  alarmLevelMap,
  AlarmLevels,
  alarmTypeMap,
  AlarmTypes,
  ChromatographyAlarmLevel,
} from 'sigma-mappings';
import { sampleFieldsDefinitions } from '@/domain/data/installation/engineering/chromatography/sample';
import translate, { formatNumber } from '@/services/translate';
import {
  formatter as dateTimeFormatter,
  getNow,
  isValid as dateTimeIsValid,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

const numberOptions = {
  maximumFractionDigits: 1,
  minimumFractionDigits: 1,
};

const urls = {
  alarmLevels: assetModuleId => `/engenharia/${assetModuleId}/cromatografia/parametros/niveis-alarmes`,
};

function getFaixaInicial(returnedField) {
  if (dateTimeIsValid(returnedField)) {
    return dateTimeFormatter(returnedField.minValue, formats.DATETIME_SHORT);
  }
  return formatNumber(returnedField.minValue, numberOptions);
}

function getFaixaFinal(returnedField) {
  if (dateTimeIsValid(returnedField)) {
    return dateTimeFormatter(getNow(), formats.DATETIME_SHORT);
  }
  return formatNumber(returnedField.maxValue, numberOptions);
}

function isGas(type) {
  return AlarmTypes.CONCENTRATION === type;
}

function isVeryHighValue(value) {
  return AlarmLevels.VERY_HIGH === value;
}

function getDataType(type, level) {
  const types = {
    'high-gas-value': isGas(type) && !isVeryHighValue(level),
    'very-high-gas-value': isGas(type) && isVeryHighValue(level),
    'high-rate-value': !isGas(type) && !isVeryHighValue(level),
    'very-high-rate-value': !isGas(type) && isVeryHighValue(level),
  };
  const typeKeys = Object.keys(types);
  return typeKeys.find(key => !!types[key]);
}

function getFieldName(data, field) {
  if (isEmpty(data.parameters)) return translate(field);
  const level = alarmLevelMap[data.parameters.nivelAlarme];
  const type = alarmTypeMap[data.parameters.tipoAlarmeCromatografia];
  if (!level || !type) return translate(field);
  const dataType = getDataType(type, level);
  const translatedDataType = translate(
    `engineering.chromatography.alarm-levels-parameters.${dataType}`,
  );
  return `${translate(field)} - ${translatedDataType}`;
}

const error = {
  errorHandling: {
    responseMessageParams: {
      $MA029: (data) => {
        const returnedField = sampleFieldsDefinitions[data.field];
        if (!returnedField) return null;

        return {
          FaixaInicial: getFaixaInicial(returnedField),
          FaixaFinal: getFaixaFinal(returnedField),
          NomeDoCampo: getFieldName(data, returnedField.name),
        };
      },
      $ME048: data => data,
    },
  },
};

export default {
  getAlarmLevelsList(assetModuleId) {
    const http = new Http();
    return http
      .get(urls.alarmLevels(assetModuleId))
      .then(({ data }) => data.map(item => new ChromatographyAlarmLevel(item)));
  },
  setAlarmLevel(assetModuleId, payload = []) {
    const mappedPayload = payload.map(item => new ChromatographyAlarmLevel.MapBack(item));
    const http = new Http(error);
    return http
      .put(urls.alarmLevels(assetModuleId), mappedPayload)
      .then(({ data }) => data);
  },
};
